import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { format } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Play,
  CheckCircle,
  Video,
  FileText,
  MessageSquare,
  Flag,
  Languages,
  Settings,
  Maximize2,
  Plus,
  Volume2,
  NotebookPen,
  X,
  MousePointer,
  HelpCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { CourseViewerDialogProps, Note } from './types';
import { markModuleComplete, addNoteToCourse } from '@/lib/firebase/queries/coursesprogress';

// Check if URL is a valid video URL (Firebase Storage or other video source)
function isVideoUrl(url: string | undefined) {
  if (!url) return false;
  return typeof url === 'string' && url.length > 0;
}

export function CourseViewerDialog({ course, open, onOpenChange, progress, userId }: CourseViewerDialogProps & { progress: any, userId: string }) {
  const [currentModule, setCurrentModule] = useState(0);
  const [currentNote, setCurrentNote] = useState('');
  const [notes, setNotes] = useState<Note[]>(progress?.notes?.filter((note: any) => note.moduleId === course.modules[0]?.id) || []);
  const { t, dir } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [completedModules, setCompletedModules] = useState<string[]>(progress?.completedModules || []);
  const [showNotes, setShowNotes] = useState(false);
  const [currentInteractiveIndex, setCurrentInteractiveIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [showModuleCompleted, setShowModuleCompleted] = useState(false);
  
  // Quiz state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<(number | null)[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizPassed, setQuizPassed] = useState(false);
  const [quizAttempts, setQuizAttempts] = useState(0);

  useEffect(() => {
    setNotes(progress?.notes?.filter((note: any) => note.moduleId === course.modules[currentModule]?.id) || []);
    setCompletedModules(progress?.completedModules || []);
    
    // Reset interactive content when module changes
    setCurrentInteractiveIndex(0);
    setDisplayedText('');
    setIsTyping(false);
    setIsCelebrating(false);
    
    // Reset quiz content when module changes
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResults(false);
    setQuizScore(0);
    setQuizPassed(false);
    
    // Check if current module is already completed
    const currentModuleData = course.modules[currentModule];
    if (currentModuleData && completedModules.includes(currentModuleData.id)) {
      setShowModuleCompleted(true);
      // For quiz modules, check if results should be shown
      if (currentModuleData.type === 'quiz' && currentModuleData.quizContent) {
        setShowResults(true);
      }
    } else {
      setShowModuleCompleted(false);
    }
  }, [currentModule, progress, course.modules, completedModules]);

  // Initialize quiz state when quiz module is loaded
  useEffect(() => {
    const currentModuleData = course.modules[currentModule];
    if (currentModuleData?.type === 'quiz' && currentModuleData.quizContent) {
      const numQuestions = currentModuleData.quizContent.questions.length;
      setSelectedAnswers(new Array(numQuestions).fill(null));
    }
  }, [currentModule, course.modules]);

  // Handle interactive text typing animation
  useEffect(() => {
    const currentModuleData = course.modules[currentModule];
    if (currentModuleData?.type === 'interactive' && currentModuleData.interactiveContent) {
      const currentText = currentModuleData.interactiveContent.texts[currentInteractiveIndex];
      if (currentText) {
        setIsTyping(true);
        setDisplayedText('');
        
        let index = 0;
        const typingInterval = setInterval(() => {
          if (index < currentText.length) {
            setDisplayedText(currentText.slice(0, index + 1));
            index++;
          } else {
            setIsTyping(false);
            clearInterval(typingInterval);
          }
        }, 50); // Adjust typing speed here

        return () => clearInterval(typingInterval);
      }
    }
  }, [currentModule, currentInteractiveIndex, course.modules]);

  const handlePrevious = () => {
    if (currentModule > 0) {
      setCurrentModule(currentModule - 1);
    }
  };

  const handleNext = () => {
    if (currentModule < course.modules.length - 1) {
      setCurrentModule(currentModule + 1);
    }
  };

  const handleAddNote = async () => {
    if (!currentNote.trim()) return;
    try {
      await addNoteToCourse(userId, course.id, course.modules[currentModule].id, currentNote.trim());
      setNotes(prev => [
        {
          id: Date.now().toString(),
          moduleIndex: currentModule,
          text: currentNote.trim(),
          timestamp: new Date().toISOString()
        },
        ...prev
      ]);
      setCurrentNote('');
      toast.success(t('education.notifications.noteSaved'));
    } catch (error) {
      toast.error(t('education.notifications.error'));
    }
  };

  const handleMarkAsComplete = async () => {
    const module = course.modules[currentModule];
    if (!module || completedModules.includes(module.id)) return;
    
    // For interactive modules, check if completion state is shown
    if (module.type === 'interactive' && module.interactiveContent) {
      if (!showModuleCompleted) {
        toast.error(t('education.notifications.viewAllTextsFirst'));
        return;
      }
    }
    
    // For quiz modules, check if quiz has been passed
    if (module.type === 'quiz' && module.quizContent) {
      if (!quizPassed || !showResults) {
        toast.error(t('education.notifications.mustPassQuiz'));
        return;
      }
    }
    
    try {
      await markModuleComplete(userId, course.id, module.id);
      setCompletedModules(prev => [...prev, module.id]);
      setShowModuleCompleted(true); // Keep the completion state visible
      toast.success(t('education.notifications.lessonCompleted'));
    } catch (error) {
      toast.error(t('education.notifications.error'));
    }
  };

  const handleNextInteractiveText = () => {
    const currentModuleData = course.modules[currentModule];
    
    // If showing module completed state, actually complete the module
    if (showModuleCompleted) {
      setIsCelebrating(true);
      
      // Show celebration toast
      toast.success(`🎉 ${t('education.moduleCompleted')}! 🎊 ${t('education.greatJob')}! 💪`, {
        duration: 3000,
      });

      // Complete the module after a brief celebration delay
      setTimeout(() => {
        handleMarkAsComplete();
        setIsCelebrating(false);
      }, 1500);
      return;
    }
    
    if (currentModuleData?.type === 'interactive' && currentModuleData.interactiveContent) {
      const nextIndex = currentInteractiveIndex + 1;
      if (nextIndex < currentModuleData.interactiveContent.texts.length) {
        setCurrentInteractiveIndex(nextIndex);
      } else {
        // All texts completed - Show "Module Completed" state
        setShowModuleCompleted(true);
        setDisplayedText('');
        setIsTyping(false);
      }
    }
  };

  // Quiz functions
  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[questionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleSubmitQuiz = () => {
    const currentModuleData = course.modules[currentModule];
    if (!currentModuleData?.quizContent) return;

    const { questions, passingScore } = currentModuleData.quizContent;
    let correctCount = 0;

    questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correctAnswer) {
        correctCount++;
      }
    });

    const score = Math.round((correctCount / questions.length) * 100);
    const passed = score >= passingScore;

    setQuizScore(score);
    setQuizPassed(passed);
    setShowResults(true);
    setQuizAttempts(prev => prev + 1);

    if (passed) {
      setShowModuleCompleted(true);
    }
  };

  const handleRetryQuiz = () => {
    const currentModuleData = course.modules[currentModule];
    if (!currentModuleData?.quizContent) return;

    const numQuestions = currentModuleData.quizContent.questions.length;
    setSelectedAnswers(new Array(numQuestions).fill(null));
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setQuizScore(0);
    setQuizPassed(false);
    setShowModuleCompleted(false);
  };

  const currentModuleTitle = `${course.title} - ${t('education.module')} ${currentModule + 1}: ${course.modules[currentModule]?.title}`;
  const content = course.modules[currentModule]?.content || '';

  // Check if Mark Complete button should be disabled
  const isMarkCompleteDisabled = () => {
    const module = course.modules[currentModule];
    if (!module) return true;
    
    // Already completed
    if (completedModules.includes(module.id)) return true;
    
    // For interactive modules, check if all texts have been viewed
    if (module.type === 'interactive' && module.interactiveContent) {
      return !showModuleCompleted;
    }
    
    // For quiz modules, check if quiz has been passed
    if (module.type === 'quiz' && module.quizContent) {
      return !quizPassed || !showResults;
    }
    
    // For other module types, use existing logic
    return false;
  };

  // Modules List Component
  const ModulesList = ({ className = "" }: { className?: string }) => (
    <div className={cn("flex flex-col h-full min-h-0", className)}>
              <div className="p-4 border-b">
                <h3 className="font-medium">{t('education.courseContent')}</h3>
              </div>
      <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden min-h-0" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                {course.modules.map((module, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentModule(index)}
                    dir={dir}
                    className={cn(
                      "w-full flex items-center gap-3 p-4 text-left hover:bg-muted/50 transition-colors",
                      currentModule === index && "bg-muted"
                    )}
                  >
                    <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center border flex-shrink-0",
                      completedModules.includes(module.id) ? "bg-[#7dda55] border-[#7dda55] text-white" : "border-muted-foreground"
                    )}>
                      {completedModules.includes(module.id) ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : module.type === 'video' ? (
                        <Video className="h-4 w-4" />
                      ) : module.type === 'reading' ? (
                        <FileText className="h-4 w-4" />
                      ) : module.type === 'quiz' ? (
                        <HelpCircle className="h-4 w-4" />
                      ) : module.type === 'interactive' ? (
                        <MousePointer className="h-4 w-4" />
                      ) : (
                        <MessageSquare className="h-4 w-4" />
                      )}
                    </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm md:text-base truncate">{module.title}</div>
                    </div>
            <div className="text-xs md:text-sm text-gray-400 flex-shrink-0">
                      {module.duration} <span className="text-xs">{dir === 'rtl' ? 'د' : 'min'}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
  );

  // Notes Section Component
  const NotesSection = ({ className = "" }: { className?: string }) => (
    <div className={cn("flex flex-col", className)}>
      <div className="p-4 border-b">
        <h3 className="font-medium">{t('education.notes')}</h3>
      </div>
      <ScrollArea className="flex-1 p-4 max-h-[300px] md:max-h-[400px]">
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="bg-muted/50 p-3 rounded-lg">
              <div className="text-sm whitespace-pre-wrap">{note.text}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {format(new Date(note.timestamp), 'PPp')}
              </div>
            </div>
          ))}
        </div>
              </ScrollArea>
      <div className="p-4 border-t">
        <div className="space-y-2">
          <Textarea
            placeholder={t('education.addNote')}
            value={currentNote}
            onChange={(e) => setCurrentNote(e.target.value)}
            className="min-h-[80px] resize-none text-sm"
          />
          <Button onClick={handleAddNote} className="w-full" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            {t('education.saveNote')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-[100vw] h-[100vh] md:max-w-[90vw] lg:max-w-[95vw] md:h-[90vh] p-0 flex flex-col bg-background overflow-hidden md:rounded-lg max-w-none rounded-none"
        hideCloseButton
      >
        <DialogTitle className="sr-only">{currentModuleTitle}</DialogTitle>
        
        {/* Header */}
        <div className="px-4 pt-4 pb-0 md:p-6 md:border-b">
          <div className="flex items-center justify-between">
            {/* Mobile Layout - Left-aligned Title */}
            <h2 className="text-lg md:text-xl font-semibold truncate flex-1 mr-4 md:hidden">
              {course.title}
            </h2>
            
            {/* Desktop Layout - Left-aligned Title */}
            <h2 className="hidden md:block text-xl font-semibold truncate flex-1 mr-4">
              {currentModuleTitle}
            </h2>
            
            {/* Desktop Close Button */}
            <div className="hidden md:flex items-center">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Mobile Action Buttons */}
            <div className="flex items-center gap-2 md:hidden">
              <Sheet open={showNotes} onOpenChange={setShowNotes}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <NotebookPen className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] p-0">
                  <NotesSection />
                </SheetContent>
              </Sheet>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Progress Bar */}
        <div className="md:hidden">
          <Progress 
            value={((currentModule + 1) / course.modules.length) * 100} 
            className="h-1 rounded-none" 
          />
        </div>

        {/* Content Area */}
        <div className={`flex-1 flex flex-col md:grid overflow-hidden ${
          course.modules[currentModule]?.type === 'quiz' 
            ? 'md:grid-cols-[300px_1fr] lg:grid-cols-[330px_1fr]' 
            : 'md:grid-cols-[300px_1fr_300px] lg:grid-cols-[330px_1fr_330px]'
        }`}>
          {/* Desktop Modules Sidebar */}
          <div className="hidden md:flex flex-col min-h-0">
            <ModulesList className="w-full border-r flex-1 min-h-0" />
          </div>

          {/* Main Content */}
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* Mobile Course Contents - Current Module Navigation */}
            <div className="md:hidden">
              <div className={`${course.modules[currentModule]?.type === 'quiz' ? 'px-3 py-1' : 'p-3'}`}>
                <div className="flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handlePrevious} 
                    disabled={currentModule === 0}
                    className="px-2"
                  >
                    {dir === 'rtl' ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronLeft className="h-4 w-4" />
                    )}
                  </Button>
                  
                  <div className="flex-1 mx-3 min-w-0">
                    <div className="flex items-center gap-2 justify-center">
                      <div className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center border flex-shrink-0",
                        completedModules.includes(course.modules[currentModule]?.id) ? "bg-[#7dda55] border-[#7dda55] text-white" : "border-muted-foreground"
                      )}>
                        {completedModules.includes(course.modules[currentModule]?.id) ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : course.modules[currentModule]?.type === 'video' ? (
                          <Video className="h-3 w-3" />
                        ) : course.modules[currentModule]?.type === 'reading' ? (
                          <FileText className="h-3 w-3" />
                        ) : course.modules[currentModule]?.type === 'quiz' ? (
                          <HelpCircle className="h-3 w-3" />
                        ) : course.modules[currentModule]?.type === 'interactive' ? (
                          <MousePointer className="h-3 w-3" />
                        ) : (
                          <MessageSquare className="h-3 w-3" />
                        )}
                      </div>
                      <div className="text-center min-w-0 flex-1">
                        <div className="flex items-center justify-center gap-2">
                          <div className="font-medium text-sm truncate">{course.modules[currentModule]?.title}</div>
                          <div className="text-xs text-muted-foreground flex-shrink-0">
                            {course.modules[currentModule]?.duration}{dir === 'rtl' ? 'د' : 'm'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleNext} 
                    disabled={currentModule === course.modules.length - 1}
                    className="px-2"
                  >
                    {dir === 'rtl' ? (
                      <ChevronLeft className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Video/Content Area */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {course.modules[currentModule]?.type === 'quiz' && course.modules[currentModule]?.quizContent ? (
                // Quiz Content Display - Full height on mobile and desktop
                <div className="flex-1 mx-0 md:mx-4 mt-1 md:mt-1 mb-0 rounded-none md:rounded-lg overflow-hidden bg-white">
                  <div className="w-full h-full flex flex-col pt-1 px-4 pb-1 md:pt-2 md:px-6 md:pb-2 min-h-0 overflow-hidden">
                    {showResults ? (
                      // Quiz Results Display
                      <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6">
                        <div className={`text-6xl ${quizPassed ? 'text-green-500' : 'text-red-500'}`}>
                          {quizPassed ? '🎉' : '😔'}
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-2xl font-bold">
                            {quizPassed ? t('education.quiz.passed') : t('education.quiz.failed')}
                          </h3>
                          <p className="text-lg text-muted-foreground">
                            {t('education.quiz.yourScore')}: {quizScore}%
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t('education.quiz.passingScore')}: {course.modules[currentModule]?.quizContent?.passingScore}%
                          </p>
                        </div>
                        {!quizPassed && course.modules[currentModule]?.quizContent?.allowRetry && (
                          <Button onClick={handleRetryQuiz} className="bg-blue-600 hover:bg-blue-700">
                            {t('education.quiz.retry')}
                          </Button>
                        )}
                      </div>
                    ) : (
                      // Quiz Questions Display
                      <>
                        <div className="flex-1 overflow-y-scroll space-y-4 md:space-y-6 [&::-webkit-scrollbar]:hidden h-0" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                          <div className="flex items-center justify-between sticky top-0 bg-white pb-2 md:pb-4">
                            <h3 className="text-base md:text-lg font-semibold">{course.modules[currentModule]?.title}</h3>
                            <div className="flex items-center gap-3">
                              <div className="text-xs md:text-sm text-muted-foreground">
                                {course.modules[currentModule]?.quizContent?.questions.length} {t('education.quiz.questions')}
                              </div>
                              <Button 
                                onClick={handleSubmitQuiz}
                                disabled={selectedAnswers.some(answer => answer === null)}
                                className="px-4 md:px-6 bg-[#7dda55] hover:bg-[#6dc447] text-sm"
                                size="sm"
                              >
                                {t('education.quiz.submit')}
                              </Button>
                            </div>
                          </div>
                          
                          {course.modules[currentModule]?.quizContent?.questions.map((question, qIndex) => (
                            <div key={question.id} className="space-y-3 md:space-y-4 p-3 md:p-4 border rounded-lg">
                              <div className="flex items-start gap-2 md:gap-3">
                                <span className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs md:text-sm font-medium">
                                  {qIndex + 1}
                                </span>
                                <p className="text-sm md:text-base font-medium leading-relaxed">{question.question}</p>
                              </div>
                              
                              <div className="space-y-2 ml-7 md:ml-9">
                                {question.options.map((option, oIndex) => (
                                  <label key={oIndex} className="flex items-start gap-2 md:gap-3 cursor-pointer py-1">
                                    <input
                                      type="radio"
                                      name={`question-${qIndex}`}
                                      checked={selectedAnswers[qIndex] === oIndex}
                                      onChange={() => handleAnswerSelect(qIndex, oIndex)}
                                      className="h-4 w-4 mt-0.5 flex-shrink-0"
                                    />
                                    <span className="text-xs md:text-sm leading-relaxed">{option}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                          
                          {/* Padding after last question */}
                          <div className="h-8 md:h-12"></div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : course.modules[currentModule]?.type === 'interactive' && course.modules[currentModule]?.interactiveContent ? (
                // Interactive Content Display - Full height on mobile, aspect ratio on desktop
                <div className={`flex-1 md:aspect-video mx-0 md:mx-4 mt-2 md:mt-4 rounded-none md:rounded-lg overflow-hidden transition-all duration-1000 ${
                  isCelebrating 
                    ? 'bg-gradient-to-br from-green-100 via-yellow-50 to-green-100 animate-pulse' 
                    : 'bg-black/5'
                }`}>
                  <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                    {isCelebrating && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-6xl animate-bounce">🎉</div>
                        <div className="text-5xl animate-ping ml-4">⭐</div>
                        <div className="text-6xl animate-bounce ml-4">🎊</div>
                      </div>
                    )}
                    <div className="flex-1 flex items-center justify-center relative z-10">
                      <div className={`max-w-2xl space-y-6 transition-transform duration-500 ${
                        isCelebrating ? 'scale-105' : 'scale-100'
                      }`}>
                        <div className="text-4xl md:text-2xl lg:text-3xl font-medium leading-relaxed min-h-[100px] flex items-center justify-center">
                                                      {showModuleCompleted ? (
                              <div className="text-center space-y-4">
                                <div className="text-4xl md:text-3xl lg:text-4xl font-bold text-green-600">
                                  🎉 {t('education.moduleCompleted')} 🎉
                                </div>
                                <div className="text-2xl md:text-xl text-muted-foreground">
                                  {t('education.greatJob')}! 💪
                                </div>
                              </div>
                          ) : (
                            <>
                              {displayedText}
                              {isTyping && <span className="animate-pulse ml-1">|</span>}
                            </>
                          )}
                        </div>
                        
                        {(!isTyping || showModuleCompleted) && (
                          <div className="space-y-4">
                            {!showModuleCompleted && (
                              <div className="text-sm text-muted-foreground">
                                {currentInteractiveIndex + 1} / {course.modules[currentModule]?.interactiveContent?.texts.length}
                              </div>
                            )}
                            {/* Only show button if not already completed in database */}
                            {!completedModules.includes(course.modules[currentModule]?.id || '') && (
                              <Button
                                onClick={handleNextInteractiveText}
                                className={`text-white px-8 py-3 transition-all duration-300 ${
                                  isCelebrating 
                                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 animate-pulse' 
                                    : showModuleCompleted
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-[#7dda55] hover:bg-[#6dc447]'
                                }`}
                                disabled={isTyping || isCelebrating}
                              >
                                {isCelebrating ? '🎉 Completing... 🎉' : showModuleCompleted ? `💪 ${t('education.completeModule')}` : `${t('education.understood')} 👍`}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ) : (
                  // Video/Other Content Display - Standard aspect ratio
                  <div className="aspect-video bg-black mx-0 md:mx-4 mt-2 md:mt-4 rounded-none md:rounded-lg overflow-hidden">
                  {isVideoUrl(course.modules[currentModule]?.videoUrl) ? (
                    <video
                      key={course.modules[currentModule]?.videoUrl}
                      src={course.modules[currentModule]?.videoUrl}
                      controls
                      className="w-full h-full object-contain"
                      controlsList="nodownload"
                      playsInline
                    >
                      {t('education.videoNotSupported')}
                    </video>
                  ) : course.modules[currentModule]?.type === 'reading' ? (
                    <div className="w-full h-full flex items-center justify-center p-8 bg-white">
                      <div className="max-w-2xl text-center">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-lg leading-relaxed whitespace-pre-wrap">{content}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-muted/50">
                      <Video className="h-16 w-16 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground text-center">
                        {t('education.noVideoAvailable')}
                      </p>
                    </div>
                  )}
                </div>
                )}

              {/* Controls - Hidden for Quiz modules */}
              {course.modules[currentModule]?.type !== 'quiz' && (
              <div className="p-2 md:p-4">
                <div className="flex flex-col gap-4">
                  {/* Desktop Navigation Buttons - Hidden on Mobile and for Quiz modules */}
                  {course.modules[currentModule]?.type !== 'quiz' && (
                  <div className="hidden md:flex items-center justify-between">
                    <div className="flex gap-2">
                      {dir === 'rtl' ? (
                        <>
                          <Button 
                            variant="outline" 
                            onClick={handleNext} 
                            disabled={currentModule === course.modules.length - 1}
                            size="sm"
                          >
                            <ChevronRight className="h-4 w-4 ml-1" />
                            <span className="hidden sm:inline">{t('education.next')}</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={handlePrevious} 
                            disabled={currentModule === 0}
                            size="sm"
                          >
                            <span className="hidden sm:inline">{t('education.previous')}</span>
                            <ChevronLeft className="h-4 w-4 mr-1" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            variant="outline" 
                            onClick={handlePrevious} 
                            disabled={currentModule === 0}
                            size="sm"
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">{t('education.previous')}</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={handleNext} 
                            disabled={currentModule === course.modules.length - 1}
                            size="sm"
                          >
                            <span className="hidden sm:inline">{t('education.next')}</span>
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Video Control Buttons - Hidden for Interactive and Quiz modules */}
                      {course.modules[currentModule]?.type !== 'interactive' && course.modules[currentModule]?.type !== 'quiz' && (
                        <>
                          <Button variant="outline" size="sm">
                        <Volume2 className="h-4 w-4" />
                      </Button>
                          <Button variant="outline" size="sm">
                        <Languages className="h-4 w-4" />
                      </Button>
                          <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                          <Button variant="outline" size="sm">
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                          <Button variant="outline" size="sm">
                        <span className="font-bold text-xs">CC</span>
                      </Button>
                          <Button variant="outline" size="sm">
                        <Flag className="h-4 w-4" />
                      </Button>
                        </>
                      )}
                      
                      <Button
                        variant="default"
                        onClick={handleMarkAsComplete}
                        disabled={isMarkCompleteDisabled()}
                        size="sm"
                        className="bg-[#7dda55] hover:bg-[#6dc447] ml-2"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">
                          {completedModules.includes(course.modules[currentModule]?.id) 
                            ? t('education.courseStatus.completed') 
                            : t('education.markAsComplete')
                          }
                        </span>
                        <span className="sm:hidden">
                          {completedModules.includes(course.modules[currentModule]?.id) ? '✓' : 'Mark'}
                        </span>
                      </Button>
                    </div>
                  </div>
                  )}

                  {/* Mobile - Controls Row - Hidden for Quiz modules */}
                  {course.modules[currentModule]?.type !== 'quiz' && (
                  <div className={`md:hidden flex items-center gap-2 ${course.modules[currentModule]?.type === 'interactive' ? 'justify-center' : 'justify-between'}`}>
                    {/* Video Control Buttons - Hidden for Interactive and Quiz modules */}
                    {course.modules[currentModule]?.type !== 'interactive' && course.modules[currentModule]?.type !== 'quiz' && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Volume2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Languages className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Maximize2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <span className="font-bold text-xs">CC</span>
                        </Button>
                        <Button variant="outline" size="sm">
                          <Flag className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Mark Complete Button - Hidden for Interactive and Quiz modules on mobile */}
                    {course.modules[currentModule]?.type !== 'interactive' && course.modules[currentModule]?.type !== 'quiz' && (
                      <Button
                        variant="default"
                        onClick={handleMarkAsComplete}
                        disabled={isMarkCompleteDisabled()}
                        size="sm"
                        className="bg-[#7dda55] hover:bg-[#6dc447] flex-shrink-0"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {completedModules.includes(course.modules[currentModule]?.id) ? '✓ Done' : 'Mark'}
                      </Button>
                    )}
                  </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Desktop Notes Sidebar - Hidden for Quiz modules */}
          {course.modules[currentModule]?.type !== 'quiz' && (
          <div className="hidden md:flex">
            <NotesSection className="w-full border-l" />
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}